import ErrorMessage from '@components/atoms/ErrorMessage';
import FormField from '@components/atoms/FormField';
import BotExplanation from '@components/molecules/BotExplanation';
import { PaymentMethod, QuoteOfferQuery } from '@graphql/generated';
import {
  InstallmentsFormResult,
  QuoteFinishForm,
} from '@lib/data/typesQuestions';
import { integerToCurrency } from '@lib/helpers/misc';
import { getPaymentName } from '@lib/helpers/texts';
import GridBox from 'components/atoms/GridBox';
import OnboardingButtonBar from 'components/molecules/button-bar/OnboardingButtonBar';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';

interface Props {
  onSubmit: (data: InstallmentsFormResult) => void;
  loading: boolean;
  item: QuoteFinishForm;
}

const generateLabelValue = (
  installment: QuoteOfferQuery['quote']['offer']['price']['installments'][0],
) => ({
  label: `${installment.number}x de ${integerToCurrency(installment.amount)} (${
    installment.hasFee ? 'com' : 'sem'
  } juros)`,
  value: installment.number,
});

const InstallmentsForm: FC<Props> = ({ onSubmit, loading, item }) => {
  const options = [
    ...new Set(item.offer?.price.installments.map((i) => i.method)),
  ].map((method) => ({
    label: getPaymentName(method),
    value: method,
  }));

  const { handleSubmit, control, errors, watch, setValue } = useForm<{
    installments: { value: string };
    method: { value: PaymentMethod };
  }>({
    mode: 'onChange',
    defaultValues: {
      method: options.length === 1 ? options[0] : undefined,
    },
  });

  const selectedMethod = watch('method')?.value;

  return (
    <form
      onSubmit={handleSubmit((data) =>
        onSubmit({
          installmentsNumber: Number(data.installments.value),
          method: data.method.value,
        }),
      )}
    >
      <GridBox
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <GridBox justifyContent="center" width={[1, 0.5, 0.5]}>
          <FormField style={{ width: '100%' }}>
            <Controller
              name="method"
              control={control}
              rules={{ required: 'Escolha o método' }}
              render={(field) => (
                <Select
                  {...field}
                  onChange={(e) => {
                    setValue('installments', null);

                    field.onChange(e);
                  }}
                  options={options}
                  placeholder="Forma de pagamento"
                  isSearchable={false}
                />
              )}
            />
            {(errors.method as { message?: string }) && (
              <ErrorMessage>
                {(errors.method as { message?: string })?.message}
              </ErrorMessage>
            )}
          </FormField>
        </GridBox>

        <GridBox justifyContent="center" width={[1, 0.5, 0.5]}>
          <FormField style={{ width: '100%' }}>
            <Controller
              name="installments"
              control={control}
              rules={{ required: 'Escolha as parcelas' }}
              render={(field) => (
                <Select
                  {...field}
                  options={item.offer?.price?.installments
                    ?.filter((i) => i.method === selectedMethod)
                    .map(generateLabelValue)}
                  placeholder="Parcelas"
                  isDisabled={!selectedMethod}
                  isSearchable={false}
                />
              )}
            />
            {(errors.installments as { message?: string }) && (
              <ErrorMessage>
                {(errors.installments as { message?: string })?.message}
              </ErrorMessage>
            )}
          </FormField>
        </GridBox>
      </GridBox>

      <BotExplanation>
        Fique tranquilo o{' '}
        <strong>
          pagamento só precisa ser feito após a aprovação da seguradora
        </strong>
        . Te avisaremos assim que tivermos o retorno.
      </BotExplanation>

      <OnboardingButtonBar loading={loading} />
    </form>
  );
};

export default InstallmentsForm;
